import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import AuthShell, {
  PasswordInput,
  PasswordMeter,
  go,
  readableError,
} from "./auth/AuthShell";

const CHANGE_PASSWORD = gql`
  mutation changeAdminPassword(
    $emailOrPhoneNumber: String!
    $password: String!
    $phoneChangePasswordCode: String!
  ) {
    changeAdminPassword(
      changePasswordInput: {
        emailOrPhoneNumber: $emailOrPhoneNumber
        password: $password
        phoneChangePasswordCode: $phoneChangePasswordCode
      }
    ) {
      message
    }
  }
`;

/**
 * ForgetPasswordChange - the last step of a password reset.
 *
 * Reached with a one-use code from the previous screen, so it fails
 * fast if that code is missing rather than sending a request that
 * cannot succeed.
 */
const ForgetPasswordChange = (props) => {
  const history = props.history;
  const identifier = window.localStorage.getItem("emailForgetPassword");
  const changePasswordCode = window.localStorage.getItem("changePasswordCode");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState("");

  const [changeAdminPassword] = useMutation(CHANGE_PASSWORD);

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving) return;

    const next = {};
    if (!password) next.password = "Choose a new password";
    else if (password.length < 8) next.password = "Use at least 8 characters";
    if (!confirmPassword) next.confirmPassword = "Repeat your new password";
    else if (password !== confirmPassword) next.confirmPassword = "The two passwords do not match";

    setErrors(next);
    setFormError("");
    if (Object.keys(next).length) return;

    setSaving(true);
    changeAdminPassword({
      variables: {
        emailOrPhoneNumber: identifier,
        password,
        phoneChangePasswordCode: changePasswordCode,
      },
    })
      .then((res) => {
        // The reset code is spent either way; leaving it behind would
        // let a reload send it again and fail confusingly.
        window.localStorage.removeItem("changePasswordCode");
        window.localStorage.removeItem("emailForgetPassword");
        setSaving(false);
        setDone(res.data.changeAdminPassword.message || "Your password has been changed.");
      })
      .catch((error) => {
        // Previously nothing re-enabled the button here, so one failed
        // attempt ended the reset.
        setSaving(false);
        setFormError(readableError(error));
      });
  };

  if (!identifier || !changePasswordCode) {
    return (
      <AuthShell
        title="Start again"
        subtitle="This reset link is no longer valid."
        brandTitle="Locked out?"
        brandText="Confirm the email or phone number on your account and you can set a new password straight away."
      >
        <div className="authForm">
          <div className="dsFormMessage warning" role="alert">
            <ErrorOutlineIcon />
            <span>Your reset code has expired or was already used. Ask for a new one.</span>
          </div>
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => go(history, "/forgetPassword")}
          >
            Back to password reset
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell
        title="Password changed"
        subtitle="Sign in with your new password."
        brandTitle="Locked out?"
        brandText="Confirm the email or phone number on your account and you can set a new password straight away."
      >
        <div className="authForm">
          <div className="dsFormMessage success" role="status">
            <CheckCircleOutlineIcon />
            <span>{done}</span>
          </div>
          <Button variant="primary" size="lg" block onClick={() => go(history, "/login")}>
            Go to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle={`For ${identifier}.`}
      brandTitle="Locked out?"
      brandText="Confirm the email or phone number on your account and you can set a new password straight away."
    >
      <form className="authForm" onSubmit={onSubmit}>
        {formError && (
          <div className="dsFormMessage error" role="alert">
            <ErrorOutlineIcon />
            <span>{formError}</span>
          </div>
        )}

        <Field label="New password" htmlFor="newPassword" error={errors.password} required>
          <PasswordInput
            id="newPassword"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        {password.length > 0 && <PasswordMeter password={password} />}

        <Field
          label="Confirm new password"
          htmlFor="confirmNewPassword"
          error={errors.confirmPassword}
          required
        >
          <PasswordInput
            id="confirmNewPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" size="lg" block loading={saving}>
          {saving ? "Saving" : "Change password"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default ForgetPasswordChange;
